import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '../components/common/Button'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { useUserStore } from '../store/userStore'
import { ensureUserProfileOnBackend, signInWithEmailPassword, signInWithGoogle, signUpWithEmailPassword } from '../services/auth'

const shouldRouteToOnboarding = (user: any): boolean => {
  if (!user) return true
  const hasLanguage = Boolean(user.targetLanguage)
  const hasDifficulty = Boolean(user.difficultyLevel)
  const hasPronunciation = Boolean(user.pronunciationStrictness)
  const hasDailyTarget = typeof user.dailyReviewTarget === 'number' && user.dailyReviewTarget > 0
  return !(hasLanguage && hasDifficulty && hasPronunciation && hasDailyTarget)
}

export function Auth() {
  const navigate = useNavigate()
  const location = useLocation()
  const { setUser } = useUserStore()

  const [mode, setMode] = useState<'signin' | 'signup'>((location.state as any)?.mode || 'signin')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const routeAfterProfile = (userProfile: any) => {
    setUser(userProfile)
    const needsOnboarding = shouldRouteToOnboarding(userProfile)
    if (needsOnboarding) {
      navigate('/onboarding')
    } else {
      navigate('/')
    }
  }

  const handleGoogleSignIn = async () => {
    setError(null)
    setIsGoogleLoading(true)
    try {
      await signInWithGoogle()
      const { user } = await ensureUserProfileOnBackend()
      routeAfterProfile(user)
    } catch (err) {
      const code = (err as { code?: string })?.code
      if (code === 'auth/popup-closed-by-user') {
        setError('Google sign-in was closed before completing. Please try again.')
      } else if (code === 'auth/cancelled-popup-request') {
        setError('Another authentication attempt is still pending. Close any extra popups and try again.')
      } else {
        setError(err instanceof Error ? err.message : 'Google sign-in failed')
      }
    } finally {
      setIsGoogleLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      if (mode === 'signin') {
        const emailTrimmed = email.trim()
        if (!emailTrimmed) {
          throw new Error('Email is required')
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
          throw new Error('Please enter a valid email address')
        }
        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters')
        }

        await signInWithEmailPassword(emailTrimmed, password)
        const { user } = await ensureUserProfileOnBackend()
        routeAfterProfile(user)
        return
      } else {
        // Basic client-side validation
        const emailTrimmed = email.trim()
        if (!emailTrimmed) {
          throw new Error('Email is required')
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
          throw new Error('Please enter a valid email address')
        }
        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters')
        }
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match')
        }

        await signUpWithEmailPassword(emailTrimmed, password)
        const { user } = await ensureUserProfileOnBackend()
        routeAfterProfile(user)
        return
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="page-shell">
      <div className="bg-[var(--color-surface)] border-b border-[var(--color-border)] px-4 py-8">
        <div className="max-w-md mx-auto">
          <h1 className="text-3xl font-semibold text-[var(--color-text)]">
            {mode === 'signin' ? 'Sign In' : 'Create Account'}
          </h1>
          <p className="text-[var(--color-muted)] mt-2">Access your learning progress and settings</p>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-10">
        <div className="bg-[var(--color-surface)] rounded-xl shadow-md p-8">

          {error && (
            <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg text-error-700">
              {error}
            </div>
          )}

          <div className="space-y-4 mb-6">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={handleGoogleSignIn}
              isLoading={isGoogleLoading}
              disabled={isLoading || isGoogleLoading}
              className="gap-3"
            >
              {!isGoogleLoading && (
                <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M23.49 12.27c0-.82-.07-1.64-.22-2.44H12v4.63h6.45a5.51 5.51 0 0 1-2.39 3.62v3h3.85c2.26-2.08 3.58-5.14 3.58-8.81Z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.85-3c-1.07.72-2.45 1.15-4.09 1.15-3.15 0-5.82-2.13-6.79-5H1.26v3.11A12 12 0 0 0 12 24Z"
                  />
                  <path
                    fill="#FABB05"
                    d="M5.21 14.24A7.2 7.2 0 0 1 4.84 12c0-.78.14-1.54.37-2.24V6.65H1.26A12 12 0 0 0 0 12c0 1.93.46 3.76 1.26 5.35l3.95-3.11Z"
                  />
                  <path
                    fill="#E94235"
                    d="M12 4.77c1.76 0 3.34.61 4.59 1.81l3.43-3.43C17.94.98 15.23 0 12 0 7.32 0 3.26 2.69 1.26 6.65l3.95 3.11c.97-2.87 3.64-5 6.79-5Z"
                  />
                </svg>
              )}
              Continue with Google
            </Button>
            <div className="flex items-center gap-3 text-xs font-medium uppercase text-[var(--color-muted)]">
              <span className="flex-1 h-px bg-[var(--color-border)]" />
              <span>Or continue with email</span>
              <span className="flex-1 h-px bg-[var(--color-border)]" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signin' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full border border-[var(--color-border)] rounded-lg px-4 py-2.5 bg-[var(--color-input)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow duration-200"
                    required
                  />
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full border border-[var(--color-border)] rounded-lg px-4 py-2.5 pr-10 bg-[var(--color-input)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow duration-200"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-9 text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors duration-200"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3.707 3.293a1 1 0 00-1.414 1.414l13 13a1 1 0 001.414-1.414l-1.853-1.853A10.451 10.451 0 0018.944 10C17.663 6.942 14.522 4.5 10 4.5a9.466 9.466 0 00-3.64.7l-2.653-2.653zM7.94 9.226a2.5 2.5 0 003.334 3.334l-3.334-3.334z" clipRule="evenodd" />
                        <path d="M5.12 7.405A10.48 10.48 0 001.056 10c1.281 3.058 4.422 5.5 8.944 5.5 1.232 0 2.394-.206 3.45-.586l-2.08-2.08A4.5 4.5 0 015.12 7.405z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 4.5c4.522 0 7.663 2.442 8.944 5.5-1.281 3.058-4.422 5.5-8.944 5.5S2.337 13.058 1.056 10C2.337 6.942 5.478 4.5 10 4.5z" />
                        <path d="M10 7.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z" />
                      </svg>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full border border-[var(--color-border)] rounded-lg px-4 py-2.5 bg-[var(--color-input)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow duration-200"
                    required
                  />
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full border border-[var(--color-border)] rounded-lg px-4 py-2.5 pr-10 bg-[var(--color-input)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow duration-200"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-9 text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors duration-200"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      // Eye-off icon
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3.707 3.293a1 1 0 00-1.414 1.414l13 13a1 1 0 001.414-1.414l-1.853-1.853A10.451 10.451 0 0018.944 10C17.663 6.942 14.522 4.5 10 4.5a9.466 9.466 0 00-3.64.7l-2.653-2.653zM7.94 9.226a2.5 2.5 0 003.334 3.334l-3.334-3.334z" clipRule="evenodd" />
                        <path d="M5.12 7.405A10.48 10.48 0 001.056 10c1.281 3.058 4.422 5.5 8.944 5.5 1.232 0 2.394-.206 3.45-.586l-2.08-2.08A4.5 4.5 0 015.12 7.405z" />
                      </svg>
                    ) : (
                      // Eye icon
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 4.5c4.522 0 7.663 2.442 8.944 5.5-1.281 3.058-4.422 5.5-8.944 5.5S2.337 13.058 1.056 10C2.337 6.942 5.478 4.5 10 4.5z" />
                        <path d="M10 7.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z" />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">Confirm Password</label>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full border border-[var(--color-border)] rounded-lg px-4 py-2.5 pr-10 bg-[var(--color-input)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow duration-200"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-9 text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors duration-200"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3.707 3.293a1 1 0 00-1.414 1.414l13 13a1 1 0 001.414-1.414l-1.853-1.853A10.451 10.451 0 0018.944 10C17.663 6.942 14.522 4.5 10 4.5a9.466 9.466 0 00-3.64.7l-2.653-2.653zM7.94 9.226a2.5 2.5 0 003.334 3.334l-3.334-3.334z" clipRule="evenodd" />
                        <path d="M5.12 7.405A10.48 10.48 0 001.056 10c1.281 3.058 4.422 5.5 8.944 5.5 1.232 0 2.394-.206 3.45-.586l-2.08-2.08A4.5 4.5 0 015.12 7.405z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 4.5c4.522 0 7.663 2.442 8.944 5.5-1.281 3.058-4.422 5.5-8.944 5.5S2.337 13.058 1.056 10C2.337 6.942 5.478 4.5 10 4.5z" />
                        <path d="M10 7.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z" />
                      </svg>
                    )}
                  </button>
                </div>
              </>
            )}

            <div className="pt-2">
              <Button type="submit" variant="primary" disabled={isLoading}>
                {isLoading ? (
                  <span className="inline-flex items-center"><LoadingSpinner size="sm" /> <span className="ml-2">Please wait...</span></span>
                ) : mode === 'signin' ? (
                  'Sign In'
                ) : (
                  'Create Account'
                )}
              </Button>
            </div>

            {/* Inline mode switch below primary button */}
            {mode === 'signin' ? (
              <p className="mt-4 text-sm text-[var(--color-muted)]">
                Don't have an account?{' '}
                <button
                  type="button"
                  className="text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200"
                  onClick={() => setMode('signup')}
                >
                  Sign up
                </button>
              </p>
            ) : (
              <p className="mt-4 text-sm text-[var(--color-muted)]">
                Already have an account?{' '}
                <button
                  type="button"
                  className="text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200"
                  onClick={() => setMode('signin')}
                >
                  Sign in
                </button>
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}

export default Auth
